import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Card, CardBody, Col, Container, Input, Row, Table } from "reactstrap";
import Select from "react-select";
import { FaArrowLeft, FaClipboardCheck, FaFileExcel, FaFilePdf, FaPlus, FaTrash } from "react-icons/fa";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import { inventoryActions } from "../../sagas/inventorySlice";
import { productActions } from "../../sagas/productSlice";
import { entrepotActions } from "../../sagas/entrepotSlice";
import stockService from "../../services/stockService";
import { preventScroll } from "../../helpers/utility";

const emptyLine = {
    articleId: "",
    articleCode: "",
    articleLibelle: "",
    quantiteTheorique: "",
    quantitePhysique: 1,
};

const InventoryFormPage = () => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { currentInventory } = useSelector((state) => state.inventory);
    const { entrepots } = useSelector((state) => state.entrepots);
    const { products, loading: productsLoading } = useSelector((state) => state.products);

    useEffect(() => {
        if (id) {
            dispatch(inventoryActions.getInventory(id));
        } else {
            dispatch(inventoryActions.clearCurrentInventory());
        }
    }, [dispatch, id]);

    useEffect(() => {
        dispatch(entrepotActions.list());
    }, [dispatch]);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: currentInventory || {
            dateInventaire: new Date().toISOString().split("T")[0],
            entrepotId: "",
            lignes: [],
        },
        onSubmit: (values) => {
            if (id) {
                dispatch(
                    inventoryActions.updateInventory({
                        id,
                        data: values,
                        onSuccess: () => navigate("/inventory-module/inventories"),
                    })
                );
            } else {
                dispatch(
                    inventoryActions.createInventory({
                        data: values,
                        onSuccess: () => navigate("/inventory-module/inventories"),
                    })
                );
            }
        },
    });

    const isLocked = currentInventory?.status === "VALIDE";
    const isEntrepotSelected = !!formik.values.entrepotId;

    const entrepotOptions =
        entrepots?.map((entrepot) => ({
            value: entrepot.id,
            label: `${entrepot.code} - ${entrepot.libelle}`,
        })) ?? [];

    const productOptions = useMemo(
        () =>
            (products || []).map((product) => ({
                value: product.id,
                label: product.libelle || product.code || `Article ${product.id}`,
                searchText: [product.code, product.libelle, product.codeBarre]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase(),
                product,
            })),
        [products]
    );

    const filterProductOption = useCallback((option, inputValue) => {
        const normalizedInput = (inputValue || "").trim().toLowerCase();
        if (!normalizedInput) {
            return true;
        }

        return (option.data.searchText || option.label || "")
            .toLowerCase()
            .includes(normalizedInput);
    }, []);

    const handleProductSearch = useCallback(
        (search) => {
            dispatch(
                productActions.findAll({
                    search: search || "",
                    page: 0,
                    size: 20,
                })
            );
        },
        [dispatch]
    );

    const handleAddLine = useCallback(() => {
        formik.setFieldValue("lignes", [...(formik.values.lignes || []), { ...emptyLine }]);
    }, [formik]);

    const handleRemoveLine = useCallback(
        (index) => {
            const updated = formik.values.lignes.filter((_, lineIndex) => lineIndex !== index);
            formik.setFieldValue("lignes", updated);
        },
        [formik]
    );

    const handleLineChange = useCallback(
        (index, field, value) => {
            const updated = [...formik.values.lignes];
            updated[index] = {
                ...updated[index],
                [field]: value,
            };
            formik.setFieldValue("lignes", updated);
        },
        [formik]
    );

    const handleProductSelect = useCallback(
        async (index, selectedOption) => {
            const updated = [...formik.values.lignes];

            if (!selectedOption?.product) {
                updated[index] = {
                    ...updated[index],
                    ...emptyLine,
                };
                formik.setFieldValue("lignes", updated);
                return;
            }

            const product = selectedOption.product;

            updated[index] = {
                ...updated[index],
                articleId: product.id || "",
                articleCode: product.codeBarre || "",
                articleLibelle: product.libelle || "",
                quantiteTheorique: product?.stockPhysique ?? 0,
                quantitePhysique:
                    updated[index].quantitePhysique !== "" &&
                    updated[index].quantitePhysique !== null &&
                    updated[index].quantitePhysique !== undefined
                        ? updated[index].quantitePhysique
                        : 1,
            };

            formik.setFieldValue("lignes", updated);
        },
        [formik]
    );

    const exportRows = useMemo(
        () =>
            (formik.values.lignes || []).map((line) => ({
                Article: line.articleLibelle || "",
                Code: line.articleCode || "",
                Theorique: Number(line.quantiteTheorique || 0),
                Physique: Number(line.quantitePhysique || 0),
                Ecart: Number(line.quantitePhysique || 0) - Number(line.quantiteTheorique || 0),
            })),
        [formik.values.lignes]
    );

    const handleExportExcel = useCallback(() => {
        if (!exportRows.length) return;

        const wb = XLSX.utils.book_new();
        const data = [
            ["Date inventaire", formik.values.dateInventaire || ""],
            ["Entrepot", entrepotOptions.find((option) => option.value === formik.values.entrepotId)?.label || ""],
            [],
            ["Article", "Code", "Theorique", "Physique", "Ecart"],
            ...exportRows.map((row) => [
                row.Article,
                row.Code,
                row.Theorique,
                row.Physique,
                row.Ecart,
            ]),
        ];

        const ws = XLSX.utils.aoa_to_sheet(data);
        XLSX.utils.book_append_sheet(wb, ws, "Inventaire");
        XLSX.writeFile(wb, `inventaire_${formik.values.dateInventaire || "export"}.xlsx`);
    }, [entrepotOptions, exportRows, formik.values.dateInventaire, formik.values.entrepotId]);

    const handleExportPdf = useCallback(() => {
        if (!exportRows.length) return;

        const doc = new jsPDF({ unit: "mm", format: "a4" });
        const entrepotLabel =
            entrepotOptions.find((option) => option.value === formik.values.entrepotId)?.label || "-";

        doc.setFontSize(16);
        doc.text("Inventaire", 14, 16);
        doc.setFontSize(10);
        doc.text(`Date: ${formik.values.dateInventaire || "-"}`, 14, 24);
        doc.text(`Entrepot: ${entrepotLabel}`, 14, 30);

        autoTable(doc, {
            startY: 36,
            head: [["Article", "Code", "Theorique", "Physique", "Ecart"]],
            body: exportRows.map((row) => [
                row.Article,
                row.Code,
                row.Theorique,
                row.Physique,
                row.Ecart,
            ]),
            styles: { fontSize: 9 },
            headStyles: { fillColor: [33, 37, 41] },
        });

        doc.save(`inventaire_${formik.values.dateInventaire || "export"}.pdf`);
    }, [entrepotOptions, exportRows, formik.values.dateInventaire, formik.values.entrepotId]);

    return (
        <Container>
            <Row className="mb-4">
                <Col>
                    <Card className="shadow-sm">
                        <CardBody className="d-flex justify-content-between align-items-center">
                            <h4 className="mb-0 d-flex align-items-center">
                                <FaClipboardCheck className="me-2 text-primary" />
                                {id ? "Modifier inventaire" : "Nouvel inventaire"}
                            </h4>
                            <div className="d-flex align-items-center gap-2">
                                <Button
                                    color="secondary"
                                    outline
                                    onClick={() => navigate("/inventory-module/inventories")}
                                >
                                    <FaArrowLeft className="me-1" />
                                    Retour
                                </Button>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {isLocked && (
                <div className="alert alert-info mt-2">
                    Cet inventaire est valide et ne peut plus etre modifie.
                </div>
            )}

            <Card>
                <CardBody>
                    <form onSubmit={formik.handleSubmit}>
                        <Row>
                            <Col md={4}>
                                <label>Date</label>
                                <Input
                                    type="date"
                                    name="dateInventaire"
                                    value={formik.values.dateInventaire}
                                    onChange={formik.handleChange}
                                    disabled={isLocked}
                                />
                            </Col>
                            <Col md={4}>
                                <label>Entrepot</label>
                                <Select
                                    id="entrepotId"
                                    options={entrepotOptions}
                                    value={entrepotOptions.find(
                                        (option) => option.value === formik.values.entrepotId
                                    )}
                                    onChange={(selectedOption) =>
                                        formik.setFieldValue("entrepotId", selectedOption?.value || "")
                                    }
                                    placeholder="Selectionner..."
                                    isSearchable
                                    isDisabled={isLocked}
                                />
                            </Col>
                        </Row>

                        <hr />

                        {!isEntrepotSelected && (
                            <div className="text-warning mb-2">
                                Selectionnez d&apos;abord un entrepot pour commencer l&apos;ajout des articles.
                            </div>
                        )}

                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="mb-0">Lignes d&apos;inventaire</h5>
                            <div className="d-flex gap-2">
                                {exportRows.length > 0 && (
                                    <>
                                        <Button
                                            type="button"
                                            color="success"
                                            size="sm"
                                            outline
                                            onClick={handleExportExcel}
                                        >
                                            <FaFileExcel className="me-1" />
                                            Export Excel
                                        </Button>
                                        <Button
                                            type="button"
                                            color="danger"
                                            size="sm"
                                            outline
                                            onClick={handleExportPdf}
                                        >
                                            <FaFilePdf className="me-1" />
                                            Export PDF
                                        </Button>
                                    </>
                                )}
                                {!isLocked && (
                                    <Button
                                        type="button"
                                        color="primary"
                                        size="sm"
                                        onClick={handleAddLine}
                                        disabled={!isEntrepotSelected}
                                    >
                                        <FaPlus className="me-1" />
                                        Ajouter ligne
                                    </Button>
                                )}
                            </div>
                        </div>

                        <Table bordered responsive>
                            <thead>
                                <tr>
                                    <th style={{ minWidth: "320px" }}>Article</th>
                                    <th style={{ minWidth: "140px" }}>Code</th>
                                    <th style={{ minWidth: "120px" }}>Theorique</th>
                                    <th style={{ minWidth: "120px" }}>Physique</th>
                                    <th style={{ minWidth: "120px" }}>Ecart</th>
                                    <th style={{ minWidth: "100px" }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {formik.values.lignes.length === 0 && (
                                    <tr>
                                        <td colSpan="6" className="text-center text-muted">
                                            Aucune ligne ajoutee
                                        </td>
                                    </tr>
                                )}

                                {formik.values.lignes.map((line, index) => (
                                    <tr key={index}>
                                        <td>
                                            <Select
                                                options={productOptions}
                                                isLoading={productsLoading}
                                                isClearable
                                                isSearchable
                                                filterOption={filterProductOption}
                                                placeholder="Scanner ou rechercher un article"
                                                value={
                                                    productOptions.find((option) => option.value === line.articleId) ||
                                                    (line.articleId
                                                        ? {
                                                              value: line.articleId,
                                                              label:
                                                                  line.articleLibelle ||
                                                                  line.articleCode ||
                                                                  `Article ${line.articleId}`,
                                                              searchText: [
                                                                  line.articleCode,
                                                                  line.articleLibelle,
                                                              ]
                                                                  .filter(Boolean)
                                                                  .join(" ")
                                                                  .toLowerCase(),
                                                              product: {
                                                                  id: line.articleId,
                                                                  codeBarre: line.articleCode,
                                                                  libelle: line.articleLibelle,
                                                              },
                                                          }
                                                        : null)
                                                }
                                                onChange={(selectedOption) =>
                                                    handleProductSelect(index, selectedOption)
                                                }
                                                onInputChange={(inputValue, meta) => {
                                                    if (meta.action === "input-change") {
                                                        handleProductSearch(inputValue);
                                                    }
                                                }}
                                                menuPortalTarget={
                                                    typeof window !== "undefined" ? document.body : null
                                                }
                                                styles={{
                                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                                }}
                                                isDisabled={!isEntrepotSelected || isLocked}
                                            />
                                        </td>
                                        <td>{line.articleCode || "-"}</td>
                                        <td>{line.quantiteTheorique === "" ? "-" : line.quantiteTheorique}</td>
                                        <td>
                                            <Input
                                                type="number"
                                                min="0"
                                                name={`lignes.${index}.quantitePhysique`}
                                                value={line.quantitePhysique}
                                                onChange={(event) =>
                                                    handleLineChange(index, "quantitePhysique", event.target.value)
                                                }
                                                onFocus={(event) => {
                                                    event.target.select();
                                                    event.target.addEventListener("wheel", preventScroll, {
                                                        passive: false,
                                                    });
                                                }}
                                                disabled={isLocked || !line.articleId}
                                            />
                                        </td>
                                        <td
                                            className={
                                                line.articleId &&
                                                (Number(line.quantitePhysique || 0) -
                                                    Number(line.quantiteTheorique || 0)) !==
                                                    0
                                                    ? "text-danger fw-bold"
                                                    : ""
                                            }
                                        >
                                            {line.articleId
                                                ? Number(line.quantitePhysique || 0) -
                                                  Number(line.quantiteTheorique || 0)
                                                : "-"}
                                        </td>
                                        <td>
                                            <Button
                                                type="button"
                                                color="danger"
                                                size="sm"
                                                onClick={() => handleRemoveLine(index)}
                                                disabled={isLocked}
                                            >
                                                <FaTrash />
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>

                        {!isLocked && (
                            <Button type="submit" color="success">
                                Enregistrer
                            </Button>
                        )}
                    </form>
                </CardBody>
            </Card>
        </Container>
    );
};

export default InventoryFormPage;
